import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService.js';
import ProjectPasswordStrengthIndicator from '../common/ProjectPasswordStrengthIndicator.jsx';

const CreateProject = () => {
  const [formData, setFormData] = useState({
    project_name: '',
    proj_password: '',
    collaborators: ['']
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projectNameValidation, setProjectNameValidation] = useState({ isValid: true, error: '' });
  
  const navigate = useNavigate();

  const validateProjectName = (name) => {
    // Allow letters, numbers, spaces, hyphens, underscores, and dots
    const validProjectNamePattern = /^[a-zA-Z0-9\s\-_.]+$/;
    
    if (!name) {
      return { isValid: true, error: '' }; // Empty is OK, required validation handles this
    }
    
    if (!validProjectNamePattern.test(name)) {
      return { 
        isValid: false, 
        error: 'Project name can only contain letters, numbers, spaces, hyphens, underscores, and dots' 
      };
    }
    
    if (name.length > 100) {
      return { 
        isValid: false, 
        error: 'Project name must be less than 100 characters' 
      };
    }
    
    return { isValid: true, error: '' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'collaborators') {
      return; // Handle collaborators separately
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Real-time validation for project_name field
    if (name === 'project_name') {
      setProjectNameValidation(validateProjectName(value));
    }
    
    setError('');
  };

  const handleCollaboratorChange = (index, value) => {
    const newCollaborators = [...formData.collaborators];
    newCollaborators[index] = value;
    setFormData({
      ...formData,
      collaborators: newCollaborators
    });
    setError('');
  };

  const addCollaborator = () => {
    setFormData({
      ...formData,
      collaborators: [...formData.collaborators, '']
    });
  };

  const removeCollaborator = (index) => {
    const newCollaborators = formData.collaborators.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      collaborators: newCollaborators.length > 0 ? newCollaborators : ['']
    });
  };

  const validateProjectPassword = (password) => {
    const errors = [];
    
    if (password.length < 6) {
      errors.push('Project password must be at least 6 characters long');
    }
    if (!/[A-Za-z]/.test(password)) {
      errors.push('Project password must contain at least one letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Project password must contain at least one number');
    }
    
    return errors;
  };

  const validateEmails = (emails) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => email.trim() && !emailRegex.test(email.trim()));
    return invalidEmails;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate project name before proceeding
    if (!projectNameValidation.isValid) {
      setError('Please fix the project name field before proceeding.');
      setLoading(false);
      return;
    }

    // Validate project password
    const passwordErrors = validateProjectPassword(formData.proj_password);
    if (passwordErrors.length > 0) {
      setError('Project password requirements:\n' + passwordErrors.map(err => `• ${err}`).join('\n'));
      setLoading(false);
      return;
    }

    // Filter out empty collaborators and validate emails
    const validCollaborators = formData.collaborators.filter(email => email.trim() !== '');
    const invalidEmails = validateEmails(validCollaborators);
    
    if (invalidEmails.length > 0) {
      setError('Invalid email addresses:\n' + invalidEmails.map(email => `• ${email}`).join('\n'));
      setLoading(false);
      return;
    }

    try {
      await projectService.createProject({
        project_name: formData.project_name,
        proj_password: formData.proj_password,
        collaborators: validCollaborators
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Create project error:', error);
      
      let errorMessage = 'Failed to create project. Please try again.';
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        if (error.response.data?.error?.includes('project_name')) {
          errorMessage = 'Project name is required and must be valid.';
        } else if (error.response.data?.error?.includes('proj_password') || error.response.data?.error?.includes('password')) {
          errorMessage = 'Project password is required and must meet security requirements.';
        } else if (error.response.data?.error?.includes('collaborators') || error.response.data?.error?.includes('email')) {
          errorMessage = 'Please provide valid email addresses for collaborators.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 409) {
        errorMessage = 'A project with this name already exists. Please choose a different name.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message && error.message !== 'Network Error') {
        errorMessage = error.message;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network connection failed. Please check your internet connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="form-container" style={{ maxWidth: '600px' }}>
        <h2>Create New Project</h2>
        
        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-triangle"></i>
            <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="project_name">Project Name:</label>
            <input
              type="text"
              id="project_name"
              name="project_name"
              value={formData.project_name}
              onChange={handleChange}
              required
              disabled={loading}
              className={!projectNameValidation.isValid ? 'input-invalid' : ''}
              style={{
                borderColor: !projectNameValidation.isValid ? '#ff4757' : undefined,
                boxShadow: !projectNameValidation.isValid ? '0 0 0 2px rgba(255, 71, 87, 0.2)' : undefined
              }}
            />
            {!projectNameValidation.isValid && (
              <div className="validation-error" style={{
                color: '#ff4757',
                fontSize: '0.85rem',
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <i className="fas fa-exclamation-triangle"></i>
                {projectNameValidation.error}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="proj_password">Project Password:</label>
            <input
              type="password"
              id="proj_password"
              name="proj_password"
              value={formData.proj_password}
              onChange={handleChange}
              required
              disabled={loading}
            />
            
            {/* Project Password Strength Indicator */}
            <ProjectPasswordStrengthIndicator password={formData.proj_password} />
          </div>

          <div className="form-group">
            <label>Collaborators (Email addresses):</label>
            {formData.collaborators.map((email, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleCollaboratorChange(index, e.target.value)}
                  placeholder="Enter email address"
                  disabled={loading}
                  style={{ flex: 1 }}
                />
                {formData.collaborators.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCollaborator(index)}
                    className="btn btn-danger"
                    disabled={loading}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addCollaborator}
              className="btn btn-secondary"
              disabled={loading}
              style={{ marginTop: '0.5rem' }}
            >
              Add Collaborator
            </button>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Creating Project...' : 'Create Project'}
            </button>
            
            <button 
              type="button" 
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProject;